import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { Panel } from "@/components/core/AppLayout";
import { CoreCalendar } from "./CoreCalendar";
import { AppointmentFormDialog } from "./AppointmentFormDialog";
import { AppointmentDetailDialog } from "./AppointmentDetailDialog";
import { SendInviteDialog } from "./SendInviteDialog";
import { useAttachmentCounts } from "@/components/core/AttachmentsPanel";
import { DeleteAppointmentDialog } from "./DeleteAppointmentDialog";

export const AppointmentsWorkspace = ({ appointments = [], reload, colorOf, titleOf, extraEvents = [], clientId, clients, users, toolbar, initialView, onRangeChange }) => {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ open: false, appt: null, defaults: null });
  const [send, setSend] = useState({ open: false, kind: "invito", appt: null, after: null });
  const [del, setDel] = useState(null);

  const attCounts = useAttachmentCounts("appointment", [appointments.length]);
  const events = [
    ...appointments.map((a) => {
      const c = colorOf(a);
      const t = titleOf ? titleOf(a) : a.titolo;
      return { id: a.id, title: attCounts[a.id] ? `${t} · 📎 ${attCounts[a.id]}` : t, start: a.inizio, end: a.fine, backgroundColor: c, borderColor: c, textColor: "#fff",
        classNames: a.stato_invito === "annullato" ? ["core-ev-cancel"] : [], extendedProps: { kind: "appointment", appt: a } };
    }),
    ...extraEvents,
  ];

  const onEventClick = (ev) => {
    const p = ev.extendedProps;
    if (p.kind === "appointment") setDetail(p.appt);
    else if (p.kind === "task") navigate(`/clienti/${p.task.client_id}?tab=task`);
    else if (p.kind === "post") navigate(`/clienti/${p.post.client_id}?tab=ped&post=${p.post.id}`);
  };

  const remove = async (a) => {
    try { await api.delete(`/appointments/${a.id}`); toast.success("Appuntamento eliminato"); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null); setDetail(null); reload();
  };

  const onSaved = (a) => {
    reload();
    if (detail) setDetail(a);
    if (a.needs_update) {
      toast.info("Data, ora o luogo modificati: invia l'aggiornamento ai destinatari.");
      setSend({ open: true, kind: "aggiornamento", appt: a, after: null });
    }
  };

  const onSent = (a) => {
    if (send.after === "delete") return remove(a);
    reload();
    if (detail) setDetail(a);
  };

  return (
    <div data-testid="appointments-workspace">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">{toolbar}</div>
        <Button onClick={() => setForm({ open: true, appt: null, defaults: null })} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold shrink-0" data-testid="add-appointment-button">
          <Plus className="h-4 w-4 mr-1" />Nuovo appuntamento
        </Button>
      </div>
      <Panel className="p-5">
        <CoreCalendar events={events} initialView={initialView} onEventClick={onEventClick} onRangeChange={onRangeChange}
          onSelect={(info) => setForm({ open: true, appt: null, defaults: { start: info.start, end: info.end, allDay: info.allDay } })} />
      </Panel>

      <AppointmentDetailDialog appt={detail} onOpenChange={(o) => !o && setDetail(null)}
        onEdit={(a) => setForm({ open: true, appt: a, defaults: null })}
        onSend={(a, kind) => setSend({ open: true, kind, appt: a, after: null })}
        onDelete={setDel} />
      <AppointmentFormDialog open={form.open} onOpenChange={(o) => setForm((p) => ({ ...p, open: o }))} appt={form.appt} defaults={form.defaults}
        clientId={clientId} clients={clients} users={users} onSaved={onSaved} />
      <SendInviteDialog open={send.open} onOpenChange={(o) => setSend((p) => ({ ...p, open: o }))} kind={send.kind} appt={send.appt} onSent={onSent} />
      <DeleteAppointmentDialog appt={del} onOpenChange={(o) => !o && setDel(null)} onDelete={remove}
        onCancelAndDelete={(a) => { setDel(null); setSend({ open: true, kind: "annullamento", appt: a, after: "delete" }); }} />
    </div>
  );
};
